import React, { useEffect, useMemo, useState } from 'react';
import Select, { ValueType } from 'react-select';
import { TeamMemberUiModel } from '../../../ui-models/team.ui-model';
import { ErrorInfo, isErrorInfo } from '../../../interfaces/error-info.interface';
import { getTeamMembers } from '../../../services/data/team.data';

interface Option {
  value: string;
  label: string;
}

const selectMenuPortalStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 })
};

interface TeamMemberPickerProps {
  value?: string;
  onChange: (userId: string | undefined) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
}

/**
 * Pick someone who can actually hold a lead.
 *
 * Deliberately not `StaffPicker`. That lists every staff account, and a lead
 * assigned to housekeeping or the design team **disappears** — lead scope is
 * resolved from the team tree, so the person holding it cannot see it and it
 * drops out of every queue silently.
 *
 * The lead service refuses these assignments now, so offering a name it will
 * reject would just be an error message waiting to happen.
 */
export const TeamMemberPicker: React.FC<TeamMemberPickerProps> = ({
  value,
  onChange,
  placeholder = 'Search team members',
  isDisabled = false,
  isClearable = false
}) => {
  const [members, setMembers] = useState<TeamMemberUiModel[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getTeamMembers().then((response: TeamMemberUiModel[] | ErrorInfo) => {
      if (cancelled) return;
      if (!isErrorInfo(response)) setMembers(response);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const options: Option[] = useMemo(
    () =>
      members.map((member: TeamMemberUiModel) => ({
        value: member.userId,
        // Team and role, not the authenticator job title — this is about who
        // can work the lead, and the team is the thing that decides that.
        label: [member.name, member.role, member.teamNames.join(', ')].filter(Boolean).join(' · ')
      })),
    [members]
  );

  const selected: Option | null =
    options.find((option: Option) => option.value === value) ??
    // Someone assigned before they left a team still has to render, or editing
    // the lead would silently clear who holds it.
    (value ? { value, label: `${value} — no longer on a team` } : null);

  return (
    <Select
      isLoading={loading}
      isDisabled={isDisabled}
      isClearable={isClearable}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={selectMenuPortalStyles}
      classNamePrefix="react-select"
      options={options}
      value={selected}
      placeholder={loading ? 'Loading team…' : placeholder}
      noOptionsMessage={() =>
        loading
          ? 'Loading team…'
          : members.length
          ? 'No team member matches that search'
          : 'Nobody is on a team yet — add members under Teams first'
      }
      onChange={(option: ValueType<Option, false>) =>
        onChange(option ? (option as Option).value : undefined)
      }
    />
  );
};
